// Textarea (DESIGN §3.6.4): wie Input, Mindesthöhe 96, wächst automatisch bis `maxRows` (10),
// Zeichenzähler und Mindestlängen-Hinweis („80 characters minimum · 46 to go“).
import { useId, useRef, useState, type Ref, type TextareaHTMLAttributes } from 'react';
import { CharCount } from './_lib/CharCount';
import { autoGrow, useAutoGrow } from './_lib/autoGrow';
import { useFieldProps } from './_lib/fieldContext';
import { mergeRefs } from './_lib/mergeRefs';
import { cn } from '../basis/kern';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  ref?: Ref<HTMLTextAreaElement>;
  /** Höchstzahl sichtbarer Zeilen, danach Scroll (Standard 10). */
  maxRows?: number;
  /** Zähler unten rechts („64 / 500“). */
  showCount?: boolean;
  /** Mindestlänge für Pflichtbegründungen; zeigt „… · n to go“, bis sie erreicht ist. */
  minChars?: number;
}

const MIN_HEIGHT = 96;

export function Textarea({
  ref,
  maxRows = 10,
  showCount = false,
  minChars,
  className,
  readOnly,
  onChange,
  ...rest
}: TextareaProps) {
  const inner = useRef<HTMLTextAreaElement>(null);
  const counterId = useId();
  const field = useFieldProps(rest);
  const controlled = rest.value !== undefined;
  const [uncontrolledLength, setUncontrolledLength] = useState(
    String(rest.defaultValue ?? '').length,
  );
  const length = controlled ? String(rest.value ?? '').length : uncontrolledLength;
  useAutoGrow(inner, rest.value, maxRows, MIN_HEIGHT);
  const counter = showCount || minChars !== undefined;

  return (
    <div className="flex w-full min-w-0 flex-col items-end gap-1">
      <textarea
        {...rest}
        ref={mergeRefs(ref, inner)}
        id={field.id}
        required={field.required}
        aria-invalid={field['aria-invalid']}
        aria-describedby={cn(field['aria-describedby'], counter && counterId) || undefined}
        readOnly={readOnly}
        rows={rest.rows ?? 3}
        onChange={(event) => {
          if (!controlled) {
            setUncontrolledLength(event.target.value.length);
            autoGrow(event.target, maxRows, MIN_HEIGHT);
          }
          onChange?.(event);
        }}
        className={cn(
          'block min-h-24 w-full resize-none rounded-lg border border-transparent bg-input px-3.5 py-2.5 text-body text-fg',
          'transition-colors duration-(--bar-dur-fast) ease-standard placeholder:text-fg-placeholder',
          '[[data-theme=light]_&]:border-line [[data-theme=light]_&]:hover:border-fg-tertiary',
          'focus-visible:outline-offset-0 aria-invalid:border-danger!',
          'disabled:cursor-not-allowed disabled:text-fg-disabled',
          readOnly && 'border-transparent! bg-transparent px-0',
          className,
        )}
      />
      {counter ? (
        <CharCount id={counterId} length={length} max={rest.maxLength} min={minChars} />
      ) : null}
    </div>
  );
}
